import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  InputGroup,
} from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [step, setStep] = useState(1); // 1 = Request Code, 2 = Verify & Set Password, 3 = Success
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");

  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const navigate = useNavigate();

  // Cooldown countdown timer for step 2
  useEffect(() => {
    let interval = null;
    if (step === 2 && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
      if (interval) clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [step, resendTimer]);

  // Step 1: Send OTP code
  const handleSendCode = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMessage("");
    setLoading(true);

    try {
      const response = await axios.post("/api/auth/forgot-password/send-code", {
        email: email.trim(),
      });

      if (response.data.maskedPhone) {
        setMaskedPhone(response.data.maskedPhone);
      }
      setInfoMessage(
        response.data.message ||
          "If an account matches that email, a verification code has been dispatched."
      );
      setStep(2);
      setResendTimer(60);
      setCanResend(false);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to request verification code. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Resend code with cooldown
  const handleResendCode = async () => {
    if (!canResend) return;
    setError("");
    setInfoMessage("");
    setLoading(true);

    try {
      const response = await axios.post("/api/auth/forgot-password/send-code", {
        email: email.trim(),
      });
      setResendTimer(60);
      setCanResend(false);
      setInfoMessage(
        response.data.message || "A new verification code has been sent."
      );
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Unable to resend code at this time. Please wait."
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 2 Submit: Verify code and update password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setInfoMessage("");

    if (!code || code.trim().length === 0) {
      setError("Please enter the 6-digit verification code.");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post("/api/auth/forgot-password/reset", {
        email: email.trim(),
        code: code.trim(),
        newPassword,
      });

      setInfoMessage(
        response.data.message || "Password has been successfully updated!"
      );
      setStep(3);

      // Save email for pre-fill on login
      localStorage.setItem("callbunker_remembered_email", email.trim());

      // Auto redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to reset password. Please check your verification code."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh", paddingTop: "40px", paddingBottom: "40px" }}
    >
      <Card style={{ width: "100%", maxWidth: "450px" }}>
        <Card.Body className="p-5">
          <div className="text-center mb-4">
            <img
              src="/icon.png"
              alt="CallBunker Logo"
              className="mb-3"
              style={{ width: "122px", height: "85px" }}
            />
            <h2>Reset Password</h2>
            <p className="text-muted">
              {step === 1 && "Enter your email to receive a recovery code"}
              {step === 2 && "Enter your verification code and new password"}
              {step === 3 && "Password updated successfully"}
            </p>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}
          {infoMessage && <Alert variant="info">{infoMessage}</Alert>}

          {/* Step 1: Request Code Form */}
          {step === 1 && (
            <Form onSubmit={handleSendCode}>
              <Form.Group className="mb-4">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter your registered email"
                  autoFocus
                />
              </Form.Group>

              <Button
                variant="primary"
                type="submit"
                className="w-100 mb-3"
                disabled={loading}
              >
                {loading ? "Sending Code..." : "Send Verification Code"}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-decoration-none">
                  &larr; Back to Login
                </Link>
              </div>
            </Form>
          )}

          {/* Step 2: Code Verification & New Password Form */}
          {step === 2 && (
            <Form onSubmit={handleResetPassword}>
              {maskedPhone && (
                <div className="text-center small text-muted mb-3">
                  Code sent to phone: <strong>{maskedPhone}</strong>
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>6-Digit Verification Code</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required
                  placeholder="123456"
                  className="text-center fw-bold fs-5 tracking-widest"
                  autoFocus
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <InputGroup className="position-relative">
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="Enter new password (min. 6 chars)"
                    className="pe-5"
                  />
                  <InputGroup.Text
                    as="button"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="border-0 bg-transparent position-absolute end-0 top-50 translate-middle-y"
                    style={{ zIndex: 10, cursor: "pointer" }}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Confirm New Password</Form.Label>
                <InputGroup className="position-relative">
                  <Form.Control
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="Confirm new password"
                    className="pe-5"
                  />
                  <InputGroup.Text
                    as="button"
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="border-0 bg-transparent position-absolute end-0 top-50 translate-middle-y"
                    style={{ zIndex: 10, cursor: "pointer" }}
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </InputGroup.Text>
                </InputGroup>
              </Form.Group>

              <div className="d-flex justify-content-between align-items-center mb-4">
                <Button
                  variant="link"
                  className="p-0 text-decoration-none small"
                  disabled={!canResend || loading}
                  onClick={handleResendCode}
                >
                  {canResend
                    ? "Resend Code"
                    : `Resend code in ${resendTimer}s`}
                </Button>
                <Button
                  variant="link"
                  className="p-0 text-decoration-none small text-muted"
                  onClick={() => {
                    setStep(1);
                    setCode("");
                  }}
                >
                  Change Email
                </Button>
              </div>

              <Button
                variant="primary"
                type="submit"
                className="w-100 mb-3"
                disabled={loading}
              >
                {loading ? "Updating Password..." : "Reset Password"}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-decoration-none">
                  &larr; Back to Login
                </Link>
              </div>
            </Form>
          )}

          {/* Step 3: Success Screen */}
          {step === 3 && (
            <div className="text-center">
              <div className="mb-4">
                <span
                  style={{
                    fontSize: "48px",
                    color: "#28a745",
                    display: "inline-block",
                  }}
                >
                  &#10004;
                </span>
              </div>
              <h5 className="mb-3">Password Updated!</h5>
              <p className="text-muted small mb-4">
                Your password has been reset successfully. You will be
                automatically redirected to the login page.
              </p>
              <Button
                variant="primary"
                className="w-100"
                onClick={() => navigate("/login")}
              >
                Proceed to Login
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ForgotPassword;
